import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "truncate" }
const _hoisted_2 = { class: "text-gray-600 font-semibold px-4 pt-2 pb-1 mr-4 flex items-center justify-between gap-2 text-base" }
const _hoisted_3 = { class: "truncate" }
const _hoisted_4 = ["checked", "disabled"]

import { Combobox, ComboboxButton, ComboboxOption, ComboboxOptions } from "@headlessui/vue";
import { PlusIcon } from "@heroicons/vue/20/solid";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useTrackEvent } from "shared/composables/tracking";
import { useCreateOrUpdateDashboard } from "@/composables/dashboard";
import { useReports } from "@/composables/report";
import { Dashboard, DashboardWidgetToAddOrRemove, Widget } from "@/types/Dashboard";
import {
  useHasPermissionToEditDashboard,
  useDashboardsWithDefault,
  useLayoutContext,
} from "@/views/dashboard/composables";
import {
  addWidgetToDashboard,
  createDashboardMenuCategories,
  removeWidgetFromDashboard,
} from "@/views/dashboard/services/projectOverviewV2Layout";


export default /*#__PURE__*/_defineComponent({
  __name: 'AddToDashboardsMenu',
  props: {
    type: {},
    arg: {}
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n();
const { layoutContext, isLoading: isLayoutContextLoading } = useLayoutContext();
const { dashboardsWithDefault, isLoading: areDashboardsLoading } =
  useDashboardsWithDefault(layoutContext);
const selectedDashboards = ref<Dashboard[]>([]);
const { createOrUpdateDashboard, isLoading: isDashboardCreatingOrUpdating } =
  useCreateOrUpdateDashboard();
const hasPermissionToEditDashboard = useHasPermissionToEditDashboard();
const { reports } = useReports();
const isLoading = computed(
  () =>
    areDashboardsLoading.value ||
    isLayoutContextLoading.value ||
    isDashboardCreatingOrUpdating.value,
);

const initializeSelectedDashboards = () => {
  selectedDashboards.value = dashboardsWithDefault.value.filter((dashboard) =>
    dashboard.widgets.some((widget) => widget.type === "ReportWidget" && widget.arg === props.arg),
  );
};

const categories = computed(() =>
  createDashboardMenuCategories(dashboardsWithDefault.value).filter(
    (category) => category.dashboards.length > 0 && hasPermissionToEditDashboard(category),
  ),
);

const canEdit = (dashboard: Dashboard) =>
  !isLoading.value && !!layoutContext.value && hasPermissionToEditDashboard(dashboard);

const trackEvent = useTrackEvent();

const update = async (dashboard: Dashboard) => {
  if (!layoutContext.value) {
    return;
  }
  const widget: DashboardWidgetToAddOrRemove = { type: props.type, arg: props.arg };

  const updatedDashboard = selectedDashboards.value.includes(dashboard)
    ? addWidgetToDashboard(dashboard, widget, layoutContext.value)
    : removeWidgetFromDashboard(dashboard, widget, layoutContext.value);

  if (updatedDashboard && props.type === "ReportWidget") {
    const report = reports.value.find((item) => item._id === props.arg);
    const reportType = report ? report.type : undefined;
    if (selectedDashboards.value.includes(dashboard) && props.type === "ReportWidget") {
      trackEvent("reports_to-dashboard_apply", { type: reportType });
    } else {
      trackEvent("reports_remove-from-dashboard_apply", { type: reportType });
    }
    return createOrUpdateDashboard(updatedDashboard);
  }
};

const handleUpdate = () => {
  Promise.allSettled(dashboardsWithDefault.value.map((dashboard) => update(dashboard))).then(
    (results) => {
      if (results.some((result) => result.status === "rejected")) {
        initializeSelectedDashboards();
      }
    },
  );
};

watch([dashboardsWithDefault, () => props.type, () => props.arg], () => {
  initializeSelectedDashboards();
});

onMounted(() => {
  initializeSelectedDashboards();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Combobox), {
    as: "div",
    multiple: "",
    modelValue: selectedDashboards.value,
    "onUpdate:modelValue": [
      _cache[0] || (_cache[0] = ($event: any) => ((selectedDashboards).value = $event)),
      handleUpdate
    ],
    class: "relative flex items-center min-w-0"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(ComboboxButton), {
        class: "rounded-md bg-white px-4 py-2 text-xs md:text-sm font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 flex gap-1 items-center truncate lg:min-w-[260px]",
        style: { height: '38px' }
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PlusIcon), { class: "w-5 h-5 shrink-0" }),
          _createElementVNode("span", _hoisted_1, _toDisplayString(_unref(t)("dashboard.add_to_dashboard")) + " (" + _toDisplayString(selectedDashboards.value.length) + ") ", 1)
        ]),
        _: 1
      }),
      (categories.value.length > 0)
        ? (_openBlock(), _createBlock(_unref(ComboboxOptions), {
            key: 0,
            class: "absolute top-10 left-0 z-10 mt-1 max-h-60 w-[275px] overflow-auto rounded-md bg-white py-1 text-base shadow-lg border ring-black ring-opacity-5 focus:outline-none sm:text-sm flex flex-col gap-3"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (category) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: category.type
                }, [
                  _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)(`dashboard.menu_categories.${category.type}`)), 1)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(category.dashboards, (dashboard) => {
                    return (_openBlock(), _createBlock(_unref(ComboboxOption), {
                      key: dashboard._id,
                      value: dashboard,
                      as: "template",
                      disabled: !canEdit(dashboard)
                    }, {
                      default: _withCtx(({ selected }) => [
                        _createElementVNode("div", {
                          class: _normalizeClass(["py-2 pl-4 pr-3 rounded-md cursor-pointer w-full flex items-center", _unref(isDashboardCreatingOrUpdating) ? '' : 'hover:bg-yellow-200'])
                        }, [
                          _createElementVNode("input", {
                            type: "checkbox",
                            checked: selected,
                            disabled: !canEdit(dashboard),
                            class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer disabled:text-gray-300"
                          }, null, 8, _hoisted_4),
                          _createElementVNode("label", {
                            class: _normalizeClass(["ml-3 text-sm truncate cursor-pointer", { 'text-gray-400': isLoading.value }])
                          }, _toDisplayString(dashboard.name), 3)
                        ], 2)
                      ]),
                      _: 2
                    }, 1032, ["value", "disabled"]))
                  }), 128))
                ]))
              }), 128))
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})